import axios from 'axios';
import offlineApi from './offlineApi';

const API_URL = '/api';

// Use the offlineApi for all API calls
// This provides offline support with caching and request queueing
const api = offlineApi.axios;

// Member API
export const memberApi = {
  getAll: () => offlineApi.get('/members/'),
  getById: (id) => offlineApi.get(`/members/${id}`),
  create: (data) => offlineApi.post('/members/', data),
  update: (id, data) => offlineApi.put(`/members/${id}`, data),
  batchUpdate: (memberIds, data) => offlineApi.post('/members/batch-update', {
    member_ids: memberIds,
    update_data: data
  }),
  batchDelete: (memberIds) => offlineApi.post('/members/batch-delete', {
    member_ids: memberIds
  }),
  delete: (id) => offlineApi.delete(`/members/${id}`),
  getPaymentStatus: (id) => offlineApi.get(`/members/${id}/payment-status`),
};

// Payment API
export const paymentApi = {
  getAll: (currentYearOnly = true) => offlineApi.get(`/payments/?current_year_only=${currentYearOnly}`),
  getByMember: (memberId, currentYearOnly = true) => offlineApi.get(`/payments/?member_id=${memberId}&current_year_only=${currentYearOnly}`),
  getById: (id) => offlineApi.get(`/payments/${id}`),
  create: (data) => offlineApi.post('/payments/', data),
  update: (id, data) => offlineApi.put(`/payments/${id}`, data),
  delete: (id) => offlineApi.delete(`/payments/${id}`),
  getForYearRange: (startYear, endYear) => offlineApi.get(`/payments/?start_year=${startYear}&end_year=${endYear}`),
  approve: (id) => offlineApi.post(`/payments/${id}/approve`),
};

// Expense API
export const expenseApi = {
  getAll: (currentYearOnly = true) => offlineApi.get(`/expenses/?current_year_only=${currentYearOnly}`),
  getByCategory: (category, currentYearOnly = true) => offlineApi.get(`/expenses/?category=${category}&current_year_only=${currentYearOnly}`),
  getByInterval: (interval, currentYearOnly = true) => offlineApi.get(`/expenses/?interval=${interval}&current_year_only=${currentYearOnly}`),
  getMonthlySpilt: (split, currentYearOnly = true) => offlineApi.get(`/expenses/?monthly_split=${split}&current_year_only=${currentYearOnly}`),
  getById: (id) => offlineApi.get(`/expenses/${id}`),
  create: (data) => offlineApi.post('/expenses/', data),
  update: (id, data) => offlineApi.put(`/expenses/${id}`, data),
  delete: (id) => offlineApi.delete(`/expenses/${id}`),
  getStatus: (id) => offlineApi.get(`/expenses/${id}/status`),
  getForYearRange: (startYear, endYear) => offlineApi.get(`/expenses/?start_year=${startYear}&end_year=${endYear}`),
};

// Expense Payment API
export const expensePaymentApi = {
  getAll: (currentYearOnly = true) => offlineApi.get(`/expense-payments/?current_year_only=${currentYearOnly}`),
  getByExpense: (expenseId, currentYearOnly = true) => offlineApi.get(`/expense-payments/?expense_id=${expenseId}&current_year_only=${currentYearOnly}`),
  getById: (id) => offlineApi.get(`/expense-payments/${id}`),
  create: (data) => offlineApi.post('/expense-payments/', data),
  update: (id, data) => offlineApi.put(`/expense-payments/${id}`, data),
  delete: (id) => offlineApi.delete(`/expense-payments/${id}`),
  getForYearRange: (startYear, endYear) => offlineApi.get(`/expense-payments/?start_year=${startYear}&end_year=${endYear}`),
  approve: (id) => offlineApi.post(`/expense-payments/${id}/approve`),
};

// Report API
export const reportApi = {
  getMonthlySummaries: (startDate, endDate) => 
    offlineApi.get(`/reports/monthly-summaries/?start_date=${startDate}&end_date=${endDate}`),
  getMemberSummaries: () => offlineApi.get('/reports/member-summaries/'),
  getExpenseSummaries: () => offlineApi.get('/reports/expense-summaries/'),
  getFinancialReport: (monthsBack = 12) => offlineApi.get(`/reports/financial-report/?months_back=${monthsBack}`),
};

// Auth API
// Note: We don't use offlineApi for authentication operations
export const authApi = {
  login: (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    return axios.post(`${API_URL}/auth/login`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  getCurrentUser: () => api.get('/auth/me'),
  createInitialUser: (userData) => axios.post(`${API_URL}/auth/setup`, userData, {
    headers: {
      'Content-Type': 'application/json'
    }
  }),
  checkSetupNeeded: () => axios.get(`${API_URL}/auth/setup-needed`),
};

// User management API
export const userApi = {
  getAll: () => offlineApi.get('/users'),
  getById: (id) => offlineApi.get(`/users/${id}`),
  create: (data) => offlineApi.post('/users', data),
  update: (id, data) => offlineApi.put(`/users/${id}`, data),
  delete: (id) => offlineApi.delete(`/users/${id}`),
};

// Audit logs API
export const auditLogApi = {
  getAll: () => offlineApi.get('/audit-logs'),
  getByUser: (userId) => offlineApi.get(`/audit-logs?user_id=${userId}`),
  getByResource: (resourceType) => offlineApi.get(`/audit-logs?resource_type=${resourceType}`),
  getByAction: (action) => offlineApi.get(`/audit-logs?action=${action}`),
  clear: () => offlineApi.delete('/audit-logs'),
};

// Data management API
export const dataApi = {
  // Export data as a zip file
  exportData: (onSuccess, onError) => {
    // Use the api instance to make the request with the auth token
    // but set the responseType to 'arraybuffer' to handle binary data
    api.get('/data/export', { 
      responseType: 'arraybuffer',
      headers: {
        'Accept': 'application/zip',
      }
    })
    .then(response => {
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/zip' });
      
      // Create a link element to download the blob
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'duebit_data.zip';
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Call the success callback
      if (onSuccess) {
        onSuccess();
      }
    })
    .catch(error => {
      console.error('Export error:', error);
      
      // Call the error callback
      if (onError) {
        onError(error);
      }
    });
  },
  
  // Import data from a zip file
  importData: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return axios.post(`${API_URL}/data/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
  },
  
  // Archive data into year-based files
  archiveData: () => {
    // This is a placeholder for the archive function
    // The actual implementation would call the archive_data.py script
    return offlineApi.post('/data/archive');
  }
};

// Performance metrics API
export const performanceApi = {
  getMetrics: () => offlineApi.get('/performance/metrics'),
  getEndpointMetrics: (endpoint) => {
    // Remove leading slash if present to prevent double slashes in URL
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    // Encode the endpoint to handle special characters
    const encodedEndpoint = encodeURIComponent(cleanEndpoint);
    return offlineApi.get(`/performance/metrics/${encodedEndpoint}`);
  },
  getSystemMetrics: () => offlineApi.get('/performance/system'),
  isEnabled: () => axios.get(`${API_URL}/performance/enabled`), // Use axios directly to avoid caching
};

// Dashboard API
export const dashboardApi = {
  getCache: () => offlineApi.get('/dashboard/cache'),
  updateCache: (data) => offlineApi.post('/dashboard/cache', data),
  refreshCache: () => offlineApi.post('/dashboard/refresh-cache'),
};

// Settings API
export const settingsApi = {
  getSettings: () => offlineApi.get('/admin/settings'),
  updateSettings: (data) => offlineApi.post('/admin/settings', data),
  rotateEncryptionKey: (data) => offlineApi.post('/admin/rotate-encryption-key', data),
  generateKey: () => offlineApi.get('/admin/generate-key'),
};

// Reminder API
export const reminderApi = {
  // Reminder settings
  getAllReminders: () => offlineApi.get('/reminders/'),
  getReminderById: (id) => offlineApi.get(`/reminders/${id}`),
  createReminder: (data) => offlineApi.post('/reminders/', data),
  updateReminder: (id, data) => offlineApi.put(`/reminders/${id}`, data),
  deleteReminder: (id) => offlineApi.delete(`/reminders/${id}`),
  toggleReminder: (id, enabled) => offlineApi.post(`/reminders/${id}/toggle`, { enabled }),
  testReminder: (id, memberId) => offlineApi.post(`/reminders/${id}/test`, { member_id: memberId }),
  processReminders: () => offlineApi.post('/reminders/process'),
  
  // Reminder templates
  getAllTemplates: () => offlineApi.get('/reminder-templates/'),
  getTemplateById: (id) => offlineApi.get(`/reminder-templates/${id}`),
  createTemplate: (data) => offlineApi.post('/reminder-templates/', data),
  updateTemplate: (id, data) => offlineApi.put(`/reminder-templates/${id}`, data),
  deleteTemplate: (id) => offlineApi.delete(`/reminder-templates/${id}`),
  getDefaultTemplate: () => offlineApi.get('/reminder-templates/default'),
  setDefaultTemplate: (id) => offlineApi.post(`/reminder-templates/${id}/set-default`),
  
  // Reminder logs
  getAllLogs: () => offlineApi.get('/reminder-logs/'),
  getLogsByMember: (memberId) => offlineApi.get(`/reminder-logs/?member_id=${memberId}`),
  getLogsByReminder: (reminderId) => offlineApi.get(`/reminder-logs/?reminder_id=${reminderId}`),
  getRecentLogs: (days = 30) => offlineApi.get(`/reminder-logs/?days=${days}`),
};

// Language API
export const languageApi = {
  getLanguages: () => offlineApi.get('/languages'),
  getCurrentLanguage: () => offlineApi.get('/language'),
  updatePreference: (language) => offlineApi.put('/language/preference', { language }),
  testTranslation: (key) => offlineApi.get(`/language/test?key=${key}`),
};

// Service API
export const serviceApi = {
  // Service management
  getAllServices: () => offlineApi.get('/services'),
  getServiceById: (id) => offlineApi.get(`/services/${id}`),
  createService: (data) => offlineApi.post('/services', data),
  updateService: (id, data) => offlineApi.put(`/services/${id}`, data),
  deleteService: (id) => offlineApi.delete(`/services/${id}`),
  
  // Import jobs
  createImportJob: (serviceId, runType) => offlineApi.post(`/services/${serviceId}/import`, { service_id: serviceId, run_type: runType }),
  getAllImportJobs: () => offlineApi.get('/services/import/jobs'),
  getImportJobsByService: (serviceId) => offlineApi.get(`/services/${serviceId}/import/jobs`),
  getImportJobById: (jobId) => offlineApi.get(`/services/import/jobs/${jobId}`),
  
  // User matching
  resolveUserConflict: (jobId, importedUser, action) => offlineApi.post(`/services/import/jobs/${jobId}/resolve`, {
    imported_user: importedUser,
    action: action
  }),
  
  // Scheduled imports
  scheduleImport: (serviceId, schedule) => offlineApi.post(`/services/${serviceId}/schedule`, schedule),
  cancelScheduledImport: (scheduleId) => offlineApi.delete(`/services/schedule/${scheduleId}`)
};

export default {
  memberApi,
  paymentApi,
  expenseApi,
  expensePaymentApi,
  reportApi,
  authApi,
  userApi,
  auditLogApi,
  dataApi,
  performanceApi,
  reminderApi,
  settingsApi,
  dashboardApi,
  languageApi,
  serviceApi,
  // Export the offlineApi for direct access if needed
  offlineApi,
  // Process any queued requests when coming back online
  processQueue: offlineApi.processQueue
};
